import { useEffect, useMemo, useState } from "react";
import { Button, Form, Image, Modal } from "react-bootstrap";
import { NumericFormat } from "react-number-format";
import Select from "react-select";
import { FaCheck, FaCopy, FaExternalLinkAlt, FaLink } from "react-icons/fa";
import {
  AddProductEntrepreneur,
  analyzeProductOfferWithAi,
  getAllPartiners,
  getAllRibbonBadges,
} from "../../services/api";
import useCategories from "../../hooks/useCategories";
import useNotification from "../../contexts/notification";
import ProductImageGalleryModal from "../ProductImageGalleryModal";
import "./styles.scss";

const PLATFORM_OPTIONS = [
  { value: "shopee", label: "Shopee", emoji: "🟠" },
  { value: "amazon", label: "Amazon", emoji: "📦" },
  { value: "ml", label: "Mercado Livre", emoji: "🟡" },
  { value: "magalu", label: "Magazine Luiza", emoji: "🔵" },
  { value: "aliexpress", label: "AliExpress", emoji: "🔴" },
];

const MESSAGE_STYLES = [
  { value: "animado", label: "Animado", emoji: "🎉" },
  { value: "apressado", label: "Apressado", emoji: "🔥" },
  { value: "formal", label: "Formal", emoji: "💼" },
  { value: "divertido", label: "Divertido", emoji: "😂" },
];

const initialForm = {
  link: "",
  product_query: "",
  name: "",
  description: "",
  previous_price: "",
  offer_price: "",
  partner_id: "",
  category_type_id: "",
  ribbon_badges: "0",
  cupom: "",
  discount_type: "pct",
  discount_value: "",
  image: null,
  image_source_url: "",
  platform_name: "",
};

function normalizeText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function formatCurrency(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return "";
  }

  return numeric.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function buildFormData(payload) {
  const data = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      data.append(key, value);
    }
  });
  return data;
}

let discountEntrySequence = 0;

function createDiscountEntry(kind) {
  discountEntrySequence += 1;

  return {
    id: `discount-${discountEntrySequence}`,
    kind,
    code: "",
    mode: "pct",
    value: "",
    limit: "",
  };
}

function getDiscountEntryTitle(kind) {
  if (kind === "pix") {
    return "Desconto Pix";
  }

  if (kind === "apply") {
    return "Cupom aplicável";
  }

  return "Cupom nominal";
}

function buildDiscountSummary(entries) {
  return entries
    .map((entry, index) => {
      const numericValue = Number(entry.value || 0);
      const discountLabel =
        numericValue > 0
          ? entry.mode === "pct"
            ? `${numericValue}%`
            : formatCurrency(numericValue)
          : "";

      if (entry.kind === "nominal") {
        const code = entry.code.trim().toUpperCase();
        return code || `CUPOM ${index + 1}`;
      }

      const baseLabel = entry.kind === "pix" ? "PIX" : `DESCONTO ${index + 1}`;
      return discountLabel ? `${baseLabel} ${discountLabel}` : baseLabel;
    })
    .filter(Boolean)
    .join(" + ");
}

function calculateDiscountedPrice(basePrice, entries) {
  return entries.reduce((total, entry) => {
    const numericValue = Number(entry.value || 0);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      return total;
    }

    let discountAmount =
      entry.mode === "pct" ? total * (numericValue / 100) : numericValue;

    const limitValue = Number(entry.limit || 0);
    if (Number.isFinite(limitValue) && limitValue > 0) {
      discountAmount = Math.min(discountAmount, limitValue);
    }

    return Math.max(total - discountAmount, 0);
  }, Math.max(basePrice, 0));
}

export default function AddProductAssistantForm() {
  const categories = useCategories();
  const { setMessage } = useNotification();
  const [form, setForm] = useState(initialForm);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [partners, setPartners] = useState([]);
  const [badges, setBadges] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [status, setStatus] = useState(null);
  const [couponEnabled, setCouponEnabled] = useState(false);
  const [discountEntries, setDiscountEntries] = useState([]);
  const [selectedMessageStyle, setSelectedMessageStyle] = useState("animado");
  const [selectedMessageVersion, setSelectedMessageVersion] = useState("1");
  const [resultModal, setResultModal] = useState({ open: false, shortUrl: "", messages: {} });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);
  const [isApplyingGalleryImage, setIsApplyingGalleryImage] = useState(false);
  const [copyState, setCopyState] = useState({ link: false, message: false });

  const groupedCategoryOptions = useMemo(
    () =>
      categories.map((category) => ({
        label: category.name,
        options: category.category_types.map((subCategory) => ({
          label: subCategory.name,
          value: String(subCategory.id),
        })),
      })),
    [categories]
  );

  const flatCategoryOptions = useMemo(
    () => groupedCategoryOptions.flatMap((group) => group.options),
    [groupedCategoryOptions]
  );

  const partnerOptions = useMemo(
    () =>
      partners.map((partner) => ({
        value: String(partner.id),
        label: partner.name,
      })),
    [partners]
  );

  const selectedPartnerOption = useMemo(
    () => partnerOptions.find((option) => option.value === String(form.partner_id || "")) || null,
    [form.partner_id, partnerOptions]
  );

  const selectedCategoryOption = useMemo(
    () => flatCategoryOptions.find((option) => option.value === String(form.category_type_id || "")) || null,
    [flatCategoryOptions, form.category_type_id]
  );

  const priceSummary = useMemo(() => {
    const previousPrice = Number(form.previous_price || 0);
    const offerPrice = Number(form.offer_price || 0);
    const basePrice = offerPrice || previousPrice || 0;
    const validEntries = discountEntries.filter((entry) => Number(entry.value || 0) > 0);
    const finalPrice = couponEnabled ? calculateDiscountedPrice(basePrice, validEntries) : basePrice;
    const totalDiscount = Math.max(basePrice - finalPrice, 0);
    const savings = previousPrice > 0 ? Math.max(previousPrice - finalPrice, 0) : totalDiscount;

    return {
      previousPrice,
      offerPrice,
      basePrice,
      finalPrice,
      totalDiscount,
      savings,
      appliedEntries: validEntries,
      appliedCount: validEntries.length,
      hasPrice: previousPrice > 0 || offerPrice > 0,
      hasFinalPrice: couponEnabled && validEntries.length > 0 && finalPrice >= 0,
    };
  }, [couponEnabled, discountEntries, form.offer_price, form.previous_price]);

  useEffect(() => {
    getAllRibbonBadges().then(setBadges);
    getAllPartiners().then(setPartners);
  }, []);

  const platformLabel = useMemo(() => {
    const found = PLATFORM_OPTIONS.find((item) => item.value === selectedPlatform);
    return found?.label || form.platform_name || "";
  }, [form.platform_name, selectedPlatform]);

  useEffect(() => {
    if (!analysis) {
      return;
    }

    const normalizedPartnerHint = normalizeText(analysis.partner_hint || platformLabel);
    const matchedPartner = !form.partner_id && normalizedPartnerHint
      ? partners.find((partner) => normalizeText(partner.name).includes(normalizedPartnerHint)) ||
        partners.find((partner) => normalizedPartnerHint.includes(normalizeText(partner.name))) ||
        null
      : null;

    const normalizedCategoryHint = normalizeText(analysis.category_hint);
    const matchedCategory = !form.category_type_id && normalizedCategoryHint
      ? flatCategoryOptions.find((option) => normalizeText(option.label).includes(normalizedCategoryHint)) ||
        flatCategoryOptions.find((option) => normalizedCategoryHint.includes(normalizeText(option.label))) ||
        null
      : null;

    if (!matchedPartner && !matchedCategory) {
      return;
    }

    setForm((current) => ({
      ...current,
      partner_id: current.partner_id || (matchedPartner ? String(matchedPartner.id) : current.partner_id),
      category_type_id: current.category_type_id || (matchedCategory ? matchedCategory.value : current.category_type_id),
    }));
  }, [analysis, flatCategoryOptions, form.category_type_id, form.partner_id, partners, platformLabel]);

  const imagePreview = useMemo(() => {
    if (form.image instanceof File) {
      return URL.createObjectURL(form.image);
    }

    if (selectedGalleryImage?.thumbnail_url || selectedGalleryImage?.url) {
      return selectedGalleryImage.thumbnail_url || selectedGalleryImage.url;
    }

    return form.image_source_url || analysis?.image_url || "";
  }, [analysis?.image_url, form.image, form.image_source_url, selectedGalleryImage]);

  useEffect(() => {
    return () => {
      if (form.image instanceof File && imagePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [form.image, imagePreview]);

  function updateForm(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function handleLinkChange(value) {
    updateForm("link", value);
    const normalized = value.toLowerCase();

    if (!selectedPlatform) {
      if (normalized.includes("shopee") || normalized.includes("shope.ee")) {
        setSelectedPlatform("shopee");
      } else if (normalized.includes("amazon") || normalized.includes("amzn")) {
        setSelectedPlatform("amazon");
      } else if (normalized.includes("mercadolivre") || normalized.includes("mercado") || normalized.includes("mlb")) {
        setSelectedPlatform("ml");
      } else if (normalized.includes("magalu") || normalized.includes("magazineluiza")) {
        setSelectedPlatform("magalu");
      } else if (normalized.includes("aliexpress") || normalized.includes("s.click.aliexpress")) {
        setSelectedPlatform("aliexpress");
      }
    }
  }

  function findPartnerByHint(partnerHint) {
    const normalizedHint = normalizeText(partnerHint || platformLabel);
    if (!normalizedHint) {
      return null;
    }

    return (
      partners.find((partner) => normalizeText(partner.name).includes(normalizedHint)) ||
      partners.find((partner) => normalizedHint.includes(normalizeText(partner.name))) ||
      null
    );
  }

  function findCategoryByHint(categoryHint) {
    const normalizedHint = normalizeText(categoryHint);
    if (!normalizedHint) {
      return null;
    }

    return (
      flatCategoryOptions.find((option) => normalizeText(option.label).includes(normalizedHint)) ||
      flatCategoryOptions.find((option) => normalizedHint.includes(normalizeText(option.label))) ||
      null
    );
  }

  async function handleAnalyzeOffer() {
    if (!form.link.trim()) {
      setMessage({ error: true, content: "Cole o link da oferta antes de preencher." });
      return;
    }

    if (!form.product_query.trim()) {
      setMessage({ error: true, content: "Informe o nome do produto para a IA localizar melhor." });
      return;
    }

    setIsAnalyzing(true);
    setStatus({ type: "loading", message: "Buscando dados do produto no link informado..." });

    try {
      const response = await analyzeProductOfferWithAi({
        link: form.link.trim(),
        product_name: form.product_query.trim(),
        platform: platformLabel || selectedPlatform || "",
      });

      const nextAnalysis = response.analysis || null;
      setAnalysis(nextAnalysis);

      updateForm("name", form.product_query.trim() || nextAnalysis?.name || "");
      if (nextAnalysis?.description) {
        updateForm("description", nextAnalysis.description);
      }
      if (nextAnalysis?.image_url && !form.image) {
        updateForm("image_source_url", nextAnalysis.image_url);
      }
      if (nextAnalysis?.partner_hint || platformLabel) {
        const matchedPartner = findPartnerByHint(nextAnalysis?.partner_hint);
        if (matchedPartner) {
          updateForm("partner_id", String(matchedPartner.id));
        }
      }
      if (nextAnalysis?.category_hint) {
        const matchedCategory = findCategoryByHint(nextAnalysis.category_hint);
        if (matchedCategory) {
          updateForm("category_type_id", matchedCategory.value);
        }
      }

      setStatus({ type: "success", message: "Produto identificado. Revise os dados, ajuste o preço e publique." });
    } catch (error) {
      console.error(error);
      setStatus({ type: "error", message: "Não foi possível preencher automaticamente agora. Você ainda pode completar manualmente." });
      setMessage({ error: true, content: "Falha ao analisar o link da oferta." });
    } finally {
      setIsAnalyzing(false);
    }
  }

  function addDiscountEntry(kind) {
    setDiscountEntries((current) => [...current, createDiscountEntry(kind)]);
  }

  function updateDiscountEntry(id, field, value) {
    setDiscountEntries((current) =>
      current.map((entry) => (entry.id === id ? { ...entry, [field]: value } : entry))
    );
  }

  function removeDiscountEntry(id) {
    setDiscountEntries((current) => current.filter((entry) => entry.id !== id));
  }

  function handleImageUpload(event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setSelectedGalleryImage(null);
    updateForm("image", file);
    updateForm("image_source_url", "");
  }

  async function copyToClipboard(type, value) {
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setCopyState((current) => ({ ...current, [type]: true }));
      setTimeout(() => {
        setCopyState((current) => ({ ...current, [type]: false }));
      }, 1800);
    } catch (error) {
      console.error(error);
      setMessage({ error: true, content: "Não foi possível copiar agora." });
    }
  }

  async function handleSelectGalleryImage(image) {
    setIsApplyingGalleryImage(true);

    try {
      const response = await fetch(image.url);
      const blob = await response.blob();
      const file = new File([blob], image.file_name || `imagem-${image.id}.jpg`, {
        type: blob.type || "image/jpeg",
      });

      updateForm("image", file);
      updateForm("image_source_url", "");
      setSelectedGalleryImage(image);
      setShowGalleryModal(false);
    } catch (error) {
      console.error(error);
      setMessage({ error: true, content: "Não foi possível usar a imagem da galeria." });
    } finally {
      setIsApplyingGalleryImage(false);
    }
  }

  function validateForm() {
    if (!selectedPlatform) {
      return "Selecione a plataforma da oferta.";
    }
    if (!form.link.trim()) {
      return "Cole o link curto da oferta.";
    }
    if (!form.name.trim()) {
      return "Preencha o nome do produto.";
    }
    if (!form.description.trim()) {
      return "Preencha uma descrição curta do produto.";
    }
    if (!form.offer_price && !form.previous_price) {
      return "Informe o preço anterior ou o preço da oferta.";
    }
    if (!form.partner_id) {
      return "Selecione o parceiro da oferta.";
    }
    if (!form.category_type_id) {
      return "Selecione a subcategoria.";
    }
    if (!form.image && !form.image_source_url) {
      return "Adicione uma imagem por upload, galeria ou pela captura automática do link.";
    }
    if (couponEnabled && discountEntries.length === 0) {
      return "Adicione pelo menos um desconto, cupom ou Pix na calculadora.";
    }
    if (couponEnabled && discountEntries.some((entry) => entry.kind === "nominal" && !entry.code.trim())) {
      return "Preencha o codigo dos cupons nominais adicionados.";
    }
    if (couponEnabled && discountEntries.some((entry) => Number(entry.value || 0) <= 0)) {
      return "Informe o valor de todos os descontos adicionados.";
    }

    return null;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const errorMessage = validateForm();
    if (errorMessage) {
      setMessage({ error: true, content: errorMessage });
      return;
    }

    setIsSubmitting(true);

    const singleDiscountEntry = discountEntries.length === 1 ? discountEntries[0] : null;
    const couponSummary = buildDiscountSummary(discountEntries);

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      image: form.image,
      image_source_url: form.image ? "" : form.image_source_url,
      previous_price: form.previous_price || "",
      link: form.link.trim(),
      partner_id: Number(form.partner_id),
      have_cupom: couponEnabled && discountEntries.length > 0 ? "1" : "0",
      send_notifications: "0",
      title: "",
      ribbon_badges: Number(form.ribbon_badges || 0),
      category_type_id: Number(form.category_type_id),
      cupom: couponEnabled ? couponSummary.toUpperCase() : "",
      cupom_validity: "",
      price_on_offer: form.offer_price ? "1" : "0",
      offer_price: form.offer_price,
      discount_type: couponEnabled && singleDiscountEntry ? singleDiscountEntry.mode : "",
      discount_value: couponEnabled && singleDiscountEntry ? singleDiscountEntry.value || "" : "",
      final_price: priceSummary.hasFinalPrice ? priceSummary.finalPrice.toFixed(2) : "",
      generate_offer_messages: "1",
      platform_name: platformLabel,
      message_highlights: (analysis?.highlights || []).join("\n"),
    };

    try {
      const response = await AddProductEntrepreneur(buildFormData(payload));
      if (response?.error || !response?.product) {
        throw response?.error || new Error("Falha ao salvar a oferta.");
      }

      setMessage({ error: false, content: response.msg || "Oferta cadastrada com sucesso." });
      setResultModal({
        open: true,
        shortUrl: response.shortUrl || "",
        messages: response.generatedMessages || {},
      });
      setCopyState({ link: false, message: false });
      setSelectedMessageStyle("animado");
      setSelectedMessageVersion("1");
      setAnalysis(null);
      setSelectedGalleryImage(null);
      setCouponEnabled(false);
      setDiscountEntries([]);
      setSelectedPlatform(null);
      setStatus(null);
      setForm(initialForm);
    } catch (error) {
      console.error(error);
      setMessage({ error: true, content: "Erro ao adicionar a oferta com IA." });
    } finally {
      setIsSubmitting(false);
    }
  }

  const availableMessageVersions = useMemo(
    () =>
      ["1", "2", "3"].filter((version) =>
        version === "1"
          ? resultModal.messages?.[selectedMessageStyle] || resultModal.messages?.[`${selectedMessageStyle}_1`]
          : resultModal.messages?.[`${selectedMessageStyle}_${version}`]
      ),
    [resultModal.messages, selectedMessageStyle]
  );

  const activeMessage =
    resultModal.messages?.[`${selectedMessageStyle}_${selectedMessageVersion}`] ||
    resultModal.messages?.[selectedMessageStyle] ||
    resultModal.messages?.[`${selectedMessageStyle}_1`] ||
    "";

  return (
    <>
      <div className="offer-assistant-page">
        <div className="offer-assistant-hero">
          <span className="offer-assistant-badge">Jersu Indica com IA</span>
          <h1>Adicionar oferta com preenchimento inteligente</h1>
          <p>
            Monte ofertas no padrao do Jersu Indica com busca no link, imagem, emblemas e mensagem pronta
            para disparar no WhatsApp em poucos cliques.
          </p>
        </div>

        <Form onSubmit={handleSubmit} className="offer-assistant-grid">
          <section className="offer-assistant-card">
            <div className="offer-assistant-title">Plataforma e busca</div>

            <div className="offer-platform-grid">
              {PLATFORM_OPTIONS.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  className={`offer-platform-button ${selectedPlatform === option.value ? "is-active" : ""}`}
                  onClick={() => {
                    setSelectedPlatform(option.value);
                    updateForm("platform_name", option.label);
                  }}
                >
                  <span>{option.emoji}</span>
                  <strong>{option.label}</strong>
                </button>
              ))}
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Link curto da oferta</Form.Label>
              <Form.Control
                type="url"
                value={form.link}
                onChange={(event) => handleLinkChange(event.target.value)}
                placeholder="Cole aqui o link da oferta"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Titulo ou modelo do produto</Form.Label>
              <div className="offer-inline-action">
                <Form.Control
                  type="text"
                  value={form.product_query}
                  onChange={(event) => updateForm("product_query", event.target.value)}
                  placeholder="Ex: Caixa de som JBL Boombox 3"
                />
                <Button type="button" onClick={handleAnalyzeOffer} disabled={isAnalyzing}>
                  {isAnalyzing ? "Preenchendo..." : "Preencher"}
                </Button>
              </div>
            </Form.Group>

            {status && <div className={`offer-status offer-status-${status.type}`}>{status.message}</div>}
          </section>

          <section className="offer-assistant-card">
            <div className="offer-assistant-title">Produto</div>

            <div className="offer-product-preview">
              <div>
                <label>Nome do produto</label>
                <Form.Control
                  type="text"
                  value={form.name}
                  onChange={(event) => updateForm("name", event.target.value)}
                  placeholder="Nome da oferta"
                />
              </div>

              <div>
                <label>Descricao curta</label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={form.description}
                  onChange={(event) => updateForm("description", event.target.value)}
                  placeholder="Resumo do produto e destaques principais"
                />
              </div>

              {analysis?.highlights?.length ? (
                <div className="offer-highlight-list">
                  {analysis.highlights.map((highlight) => (
                    <span key={highlight}>{highlight}</span>
                  ))}
                </div>
              ) : null}
            </div>
          </section>

          <section className="offer-assistant-card">
            <div className="offer-assistant-title">Preco e cupom</div>

            <div className="offer-price-grid">
              <div>
                <label>Preco anterior</label>
                <NumericFormat
                  value={form.previous_price}
                  customInput={Form.Control}
                  thousandSeparator="."
                  decimalSeparator="," 
                  allowNegative={false}
                  decimalScale={2}
                  fixedDecimalScale={false}
                  onValueChange={({ floatValue }) => updateForm("previous_price", floatValue ?? "")}
                  placeholder="0,00"
                />
              </div>
              <div>
                <label>Preco da oferta</label>
                <NumericFormat
                  value={form.offer_price}
                  customInput={Form.Control}
                  thousandSeparator="."
                  decimalSeparator="," 
                  allowNegative={false}
                  decimalScale={2}
                  fixedDecimalScale={false}
                  onValueChange={({ floatValue }) => updateForm("offer_price", floatValue ?? "")}
                  placeholder="0,00"
                />
              </div>
            </div>

            <div className="offer-toggle-row">
              <div>
                <strong>Tem cupom?</strong>
                <p>Se houver, a mensagem final já sai com o preco calculado.</p>
              </div>
              <Form.Check
                type="switch"
                id="assistant-coupon-switch"
                checked={couponEnabled}
                onChange={(event) => {
                  const isEnabled = event.target.checked;
                  setCouponEnabled(isEnabled);
                  if (!isEnabled) {
                    setDiscountEntries([]);
                  }
                }}
              />
            </div>

            {couponEnabled ? (
              <div className="offer-coupon-box">
                <div className="offer-discount-toolbar">
                  <Button type="button" variant="dark" className="offer-discount-add" onClick={() => addDiscountEntry("nominal")}>
                    + Cupom nominal
                  </Button>
                  <Button type="button" variant="dark" className="offer-discount-add" onClick={() => addDiscountEntry("apply")}>
                    + Cupom aplicável
                  </Button>
                  <Button type="button" variant="dark" className="offer-discount-add" onClick={() => addDiscountEntry("pix")}>
                    + Desconto Pix
                  </Button>
                </div>

                {discountEntries.length ? (
                  <div className="offer-discount-entry-list">
                    {discountEntries.map((entry) => (
                      <div key={entry.id} className="offer-discount-entry">
                        <div className="offer-discount-entry-header">
                          <strong>{getDiscountEntryTitle(entry.kind)}</strong>
                          <button
                            type="button"
                            className="offer-discount-remove"
                            onClick={() => removeDiscountEntry(entry.id)}
                          >
                            Remover
                          </button>
                        </div>

                        {entry.kind === "nominal" ? (
                          <div>
                            <label>Código do cupom</label>
                            <Form.Control
                              type="text"
                              value={entry.code}
                              onChange={(event) => updateDiscountEntry(entry.id, "code", event.target.value.toUpperCase())}
                              placeholder="MEGA10"
                            />
                          </div>
                        ) : (
                          <p className="offer-discount-entry-meta">
                            {entry.kind === "pix"
                              ? "Desconto extra aplicado no pagamento por Pix."
                              : "Desconto adicional para combinar com a oferta."}
                          </p>
                        )}

                        <div className="offer-discount-entry-grid">
                          <div className="offer-discount-type">
                            <button
                              type="button"
                              className={entry.mode === "pct" ? "is-active" : ""}
                              onClick={() => updateDiscountEntry(entry.id, "mode", "pct")}
                            >
                              % Porcentagem
                            </button>
                            <button
                              type="button"
                              className={entry.mode === "brl" ? "is-active" : ""}
                              onClick={() => updateDiscountEntry(entry.id, "mode", "brl")}
                            >
                              R$ Valor fixo
                            </button>
                          </div>

                          <div>
                            <label>{entry.mode === "pct" ? "Quanto desconta (%)" : "Quanto desconta (R$)"}</label>
                            <NumericFormat
                              value={entry.value}
                              customInput={Form.Control}
                              thousandSeparator="."
                              decimalSeparator="," 
                              allowNegative={false}
                              decimalScale={2}
                              fixedDecimalScale={false}
                              onValueChange={({ floatValue }) => updateDiscountEntry(entry.id, "value", floatValue ?? "")}
                              placeholder={entry.mode === "pct" ? "10" : "20,00"}
                            />
                          </div>

                          {entry.kind !== "pix" ? (
                            <div>
                              <label>Limite máximo (R$)</label>
                              <NumericFormat
                                value={entry.limit}
                                customInput={Form.Control}
                                thousandSeparator="."
                                decimalSeparator="," 
                                allowNegative={false}
                                decimalScale={2}
                                fixedDecimalScale={false}
                                onValueChange={({ floatValue }) => updateDiscountEntry(entry.id, "limit", floatValue ?? "")}
                                placeholder="Sem limite"
                              />
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="offer-discount-empty">Nenhum desconto adicionado ainda.</div>
                )}
              </div>
            ) : null}

            {priceSummary.hasPrice ? (
              <div className="offer-price-summary offer-price-summary-detailed">
                <div className="offer-price-summary-top">
                  <div>
                    <small>Economia total</small>
                    <strong>- {formatCurrency(priceSummary.totalDiscount)}</strong>
                  </div>
                  <div className="offer-price-summary-main">
                    <small>Preço final</small>
                    <strong>{formatCurrency(priceSummary.hasFinalPrice ? priceSummary.finalPrice : priceSummary.basePrice)}</strong>
                  </div>
                </div>

                <div className="offer-price-summary-divider" />

                <div className="offer-price-summary-bottom">
                  <span>Itens: {priceSummary.appliedCount}</span>
                  <span>Subtotal: {formatCurrency(priceSummary.basePrice)}</span>
                  {priceSummary.previousPrice > 0 ? <span>De: {formatCurrency(priceSummary.previousPrice)}</span> : null}
                </div>
              </div>
            ) : null}
          </section>

          <section className="offer-assistant-card">
            <div className="offer-assistant-title">Imagem, parceiro e emblema</div>

            <div className="offer-image-actions">
              <Button type="button" variant="outline-primary" onClick={() => document.getElementById("assistant-image-input")?.click()}>
                Fazer upload
              </Button>
              <Button type="button" variant="outline-secondary" onClick={() => setShowGalleryModal(true)}>
                Usar galeria
              </Button>
              <input
                id="assistant-image-input"
                type="file"
                accept="image/avif,image/webp,image/png,image/jpeg"
                hidden
                onChange={handleImageUpload}
              />
            </div>

            {imagePreview ? (
              <div className="offer-image-preview">
                <Image src={imagePreview} alt={form.name || "Imagem da oferta"} fluid rounded />
                {selectedGalleryImage?.product_name ? <small>{selectedGalleryImage.product_name}</small> : null}
              </div>
            ) : (
              <div className="offer-image-empty">Nenhuma imagem selecionada ainda.</div>
            )}

            <div className="offer-select-grid">
              <div>
                <label>Parceiro</label>
                <Select
                  inputId="assistant-partner-id"
                  name="partner_id"
                  options={partnerOptions}
                  value={selectedPartnerOption}
                  onChange={(option) => updateForm("partner_id", option ? option.value : "")}
                  placeholder="Selecione um parceiro"
                  noOptionsMessage={() => "Nenhum parceiro encontrado"}
                  classNamePrefix="select"
                  isClearable
                />
              </div>

              <div>
                <label>Subcategoria</label>
                <Select
                  inputId="assistant-category-type-id"
                  name="category_type_id"
                  options={groupedCategoryOptions}
                  value={selectedCategoryOption}
                  onChange={(option) => updateForm("category_type_id", option ? option.value : "")}
                  placeholder="Selecione uma subcategoria"
                  noOptionsMessage={() => "Nenhuma subcategoria encontrada"}
                  classNamePrefix="select"
                  isClearable
                />
              </div>
            </div>

            <div>
              <label>Emblema</label>
              <div className="offer-badge-grid">
                <button
                  type="button"
                  className={form.ribbon_badges === "0" ? "is-active" : ""}
                  onClick={() => updateForm("ribbon_badges", "0")}
                >
                  Sem emblema
                </button>
                {badges.map((badge) => (
                  <button
                    type="button"
                    key={badge.id}
                    className={form.ribbon_badges === String(badge.id) ? "is-active" : ""}
                    onClick={() => updateForm("ribbon_badges", String(badge.id))}
                  >
                    {badge.name}
                  </button>
                ))}
              </div>
            </div>
          </section>

          <section className="offer-assistant-card">
            <div className="offer-assistant-title">Mensagem</div>

            <div className="offer-style-grid">
              {MESSAGE_STYLES.map((style) => (
                <button
                  type="button"
                  key={style.value}
                  className={selectedMessageStyle === style.value ? "is-active" : ""}
                  onClick={() => setSelectedMessageStyle(style.value)}
                >
                  <span>{style.emoji}</span>
                  <strong>{style.label}</strong>
                </button>
              ))}
            </div>

            <p className="offer-message-note">
              A janelinha final vai abrir com as mensagens prontas para copiar, já usando o link curto do
              post cadastrado.
            </p>

            <div className="offer-submit-row">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Adicionando oferta..." : "Adicionar oferta com IA"}
              </Button>
            </div>
          </section>
        </Form>
      </div>

      <Modal show={resultModal.open} onHide={() => setResultModal((current) => ({ ...current, open: false }))} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Oferta publicada e mensagem pronta</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="offer-result-shorturl">
            <label>Link curto do post</label>
            <div>
              <div className={`offer-copy-input ${copyState.link ? "is-copied" : ""}`}>
                <span className="offer-copy-input-icon">
                  <FaLink />
                </span>
                <Form.Control value={resultModal.shortUrl} readOnly />
              </div>
              <Button
                type="button"
                className={`offer-copy-button ${copyState.link ? "is-copied" : ""}`}
                onClick={() => copyToClipboard("link", resultModal.shortUrl || "")}
              >
                {copyState.link ? <FaCheck /> : <FaCopy />}
                <span>{copyState.link ? "Link copiado" : "Copiar link"}</span>
              </Button>
            </div>
          </div>

          <div className="offer-style-grid offer-style-grid-modal">
            {MESSAGE_STYLES.filter(
              (style) =>
                resultModal.messages?.[style.value] ||
                resultModal.messages?.[`${style.value}_1`] ||
                resultModal.messages?.[`${style.value}_2`] ||
                resultModal.messages?.[`${style.value}_3`]
            ).map((style) => (
              <button
                type="button"
                key={style.value}
                className={selectedMessageStyle === style.value ? "is-active" : ""}
                onClick={() => {
                  setSelectedMessageStyle(style.value);
                  setSelectedMessageVersion("1");
                }}
              >
                <span>{style.emoji}</span>
                <strong>{style.label}</strong>
              </button>
            ))}
          </div>

          {availableMessageVersions.length > 0 ? (
            <div className="offer-message-version-grid">
              {availableMessageVersions.map((version) => (
                <button
                  type="button"
                  key={version}
                  className={`offer-message-version-button ${selectedMessageVersion === version ? "is-active" : ""}`}
                  onClick={() => setSelectedMessageVersion(version)}
                >
                  Versão {version}
                </button>
              ))}
            </div>
          ) : null}

          <div className="offer-result-message">{activeMessage || "A mensagem será exibida aqui."}</div>
        </Modal.Body>
        <Modal.Footer className="offer-modal-footer">
          <Button
            type="button"
            variant={copyState.message ? "success" : "outline-primary"}
            className={`offer-copy-button offer-copy-message-button ${copyState.message ? "is-copied" : ""}`}
            onClick={() => copyToClipboard("message", activeMessage || "")}
          >
            {copyState.message ? <FaCheck /> : <FaCopy />}
            <span>{copyState.message ? "Mensagem copiada" : "Copiar mensagem"}</span>
          </Button>
          <Button type="button" className="offer-open-post-button" onClick={() => window.open(resultModal.shortUrl, "_blank")}>
            <FaExternalLinkAlt />
            <span>Ver post</span>
          </Button>
        </Modal.Footer>
      </Modal>

      <ProductImageGalleryModal
        show={showGalleryModal}
        onHide={() => !isApplyingGalleryImage && setShowGalleryModal(false)}
        onSelect={handleSelectGalleryImage}
        selectedImageId={selectedGalleryImage?.id}
        isConfirmingSelection={isApplyingGalleryImage}
      />
    </>
  );
}
